import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";

/**
 * `GlassButton` — translucent glass button shared between the ChatV2
 * header (expand / close), the NotificationPanel header (close), and
 * any future panel header that wants the same family.
 *
 * Two shapes:
 *   - `round` (default): 32×32 circle, used for icon-only actions like
 *     close, expand, collapse.
 *   - `pill`: auto-width pill with optional leading content (icon +
 *     label), used for the "Mark all read" button on notification
 *     panels.
 *
 * Both share the same surface chrome: `rgba(255,255,255,0.3)` fill +
 * `blur(8px)` backdrop + 1px `--d-rule` border + `--d-ink-muted` icon.
 * Hover brightens to `rgba(255,255,255,0.6)` + ink colour.
 */
type Shape = "round" | "pill";

interface GlassButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  shape?: Shape;
  children?: ReactNode;
  /** Diameter in px for `round` shape. Default 32. */
  size?: number;
  /** Override the default min-width on `pill` shape. */
  pillMinWidth?: number;
}

const BASE_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.3)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  border: "1px solid var(--d-rule)",
  color: "var(--d-ink-muted)",
  cursor: "pointer",
  transition:
    "background 200ms cubic-bezier(0.32,0.72,0,1), color 200ms cubic-bezier(0.32,0.72,0,1)",
  fontFamily: "var(--d-font-sans)",
};

const PILL_SIZE: CSSProperties = {
  height: 32,
  padding: "0 12px",
  // Match the filter-select pill radius (`.d-ctrl-pill` → var(--d-r-lg),
  // 8px) so the "Mark all read" button shares the segmented-control /
  // select rounding instead of a full 999 pill.
  borderRadius: "var(--d-r-lg)",
  fontSize: 12,
  fontWeight: 500,
  gap: 6,
};

export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  function GlassButton(
    {
      shape = "round",
      children,
      style,
      size = 32,
      pillMinWidth,
      type = "button",
      ...rest
    },
    ref,
  ) {
    const merged: CSSProperties = {
      ...BASE_STYLE,
      ...(shape === "round"
        ? { width: size, height: size, borderRadius: 999 }
        : PILL_SIZE),
      ...(pillMinWidth ? { minWidth: pillMinWidth } : undefined),
      ...style,
    };
    return (
      <button
        ref={ref}
        type={type}
        style={merged}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.6)";
          e.currentTarget.style.color = "var(--d-ink)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.3)";
          e.currentTarget.style.color = "var(--d-ink-muted)";
        }}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
